"use client";

import { useCallback, useMemo, useState } from "react";
import { FEATURE_FLAGS, METRICS_EVENTS } from "@cede/types";
import { formatBalance } from "@cede/utils";
import { getDayLabel } from "@/components/features/leaderboards/epoch-leaderboard/utils";
import { RewardsModalContent } from "@/components/features/rewards/Reward/RewardsModalContent";
import { GiftBoxBottomIcon, GiftBoxTopIcon, GiftPandaIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useTelegramContext } from "@/contexts/TelegramContext";
import { useClaimTTEHTERewards, ClaimRewardsError, RewardEpochDetails } from "@/hooks";
import { useMerklRewards } from "@/hooks/campaign/useMerklRewards";
import { useCashbackStatus } from "@/hooks/cashback/useCashbackStatus";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { CLAIM_REWARDS_ERROR_TYPE } from "@/hooks/user/useClaimRewards";
import { cn } from "@/lib/utils";
import { useCashbackDialogStore } from "@/store/cashbackDialogStore";
import Decimal from "decimal.js";
import { BadgePercent, ChevronRight, Gift } from "lucide-react";
import { useSnackbar } from "notistack";

type TreasureChestButtonProps = {
  className?: string;
};

const ClaimAnimatedIcon = () => (
  <div className="claim-animated-icon relative flex flex-col items-center leading-none -translate-y-[1px] scale-[0.9]">
    <div className="relative z-20">
      <GiftBoxTopIcon />
    </div>
    <div className="absolute inset-x-0 top-[4px] flex h-[10px] items-center justify-center overflow-hidden">
      <div className="relative scale-[0.9]">
        <GiftPandaIcon />
      </div>
    </div>
    <div className="relative z-10 -translate-y-[3px] transform">
      <GiftBoxBottomIcon />
    </div>
  </div>
);

export const TreasureChestButton = ({ className }: TreasureChestButtonProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { isTMA } = useTelegramContext();
  const { handleOpen: openCashback } = useCashbackDialogStore();
  const { status: cashbackStatus } = useCashbackStatus();
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const isClaimsEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.CLAIMS_HUB_ENABLE);
  const { trackEvent } = useMetricsEvents();

  const {
    totalRewardAmountFiat,
    rewardChainIds,
    address,
    rewardDetails,
    claimReward,
    isLoading,
    isClaimCooldownActive,
    claimableSoonRewardDetails,
    claimablesData,
    batchProgress,
    totalBatches,
  } = useClaimTTEHTERewards({
    onClaimSuccess: () => {
      setIsOpen(false);
      enqueueSnackbar("Rewards claimed successfully", { variant: "success" });
    },
    onClaimError: (error: ClaimRewardsError) => {
      if (error.type === CLAIM_REWARDS_ERROR_TYPE.USER_REJECTED) return;
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  // The API only accepts USDT for Merkl campaigns, so Merkl amounts add 1:1 to dollars.
  const { totalClaimable: merklClaimable } = useMerklRewards();

  const campaignRewardsClaimable = useMemo(
    () => new Decimal(totalRewardAmountFiat).plus(merklClaimable),
    [totalRewardAmountFiat, merklClaimable],
  );

  const cashbackClaimable = useMemo(() => {
    if (!cashbackStatus?.claimable?.amount) return new Decimal(0);
    return new Decimal(cashbackStatus.claimable.amount);
  }, [cashbackStatus?.claimable?.amount]);

  const totalClaimable = useMemo(
    () => campaignRewardsClaimable.plus(cashbackClaimable),
    [campaignRewardsClaimable, cashbackClaimable],
  );

  const chestState = useMemo(() => {
    if (totalClaimable.lte(0)) return "empty";
    if (totalClaimable.gte(100)) return "full";
    return "partial";
  }, [totalClaimable]);

  const populateRewardDetails = useCallback((rewardDetailsMap: Map<string, RewardEpochDetails[]>) => {
    const result: {
      campaignData: {
        id: string;
        name: string;
        icon: string | null;
        title: string;
      };
      rewardDetails: (RewardEpochDetails & { epochLabel: string })[];
    }[] = [];

    for (const epochs of rewardDetailsMap.values()) {
      const populatedRewardDetails = epochs
        .map((epochData) => {
          const epochLabel = getDayLabel(epochData.activeEpochId, epochData.epochStart, epochData.epochEnd);
          return {
            ...epochData,
            epochLabel,
          };
        })
        .filter((v) => !!v);

      result.push({
        campaignData: epochs[0]?.campaignData || { id: "0", name: "", icon: null, title: "" },
        rewardDetails: populatedRewardDetails,
      });
    }

    return result;
  }, []);

  const populatedRewardsDetails = useMemo(() => {
    return populateRewardDetails(rewardDetails);
  }, [rewardDetails, populateRewardDetails]);

  const populatedClaimableSoonRewardsDetails = useMemo(() => {
    return populateRewardDetails(claimableSoonRewardDetails);
  }, [claimableSoonRewardDetails, populateRewardDetails]);

  const hasClaimable = totalClaimable.gt(0);
  const amountText = formatBalance(totalClaimable.toNumber(), { roundForFiat: true });

  const rewardsAmountText = formatBalance(campaignRewardsClaimable.toNumber(), { roundForFiat: true });
  const cashbackAmountText = formatBalance(cashbackClaimable.toNumber(), { roundForFiat: true });

  if (!address) {
    return null;
  }

  return (
    <>
      <Popover
        open={isDropdownOpen}
        onOpenChange={(open) => {
          setIsDropdownOpen(open);
          if (open) {
            trackEvent(METRICS_EVENTS.HEADER_REWARDS_MENU_OPENED, {
              totalClaimable: totalClaimable.toNumber(),
              hasClaimable,
              source: "header",
            });
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="secondary"
            size="small"
            className={cn(
              "claim-button-web claim-button-web--animated",
              "relative min-w-0 gap-2 px-3",
              "transition-colors duration-200",
              chestState === "empty" && "opacity-70",
              chestState === "full" && "shadow-[0_0_12px_rgba(94,166,255,0.25)]",
              className,
            )}
            aria-label="Rewards"
            disabled={!isClaimsEnabled}
          >
            <ClaimAnimatedIcon />
            {hasClaimable ? (
              <span className="text-sm font-semibold flex items-center gap-1">
                <span className="text-cede-primary">Claim now:</span>
                <span className="text-white flex items-center gap-1">${amountText}</span>
              </span>
            ) : (
              <span className="text-sm font-semibold text-cede-text-primary">Rewards</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-64 p-3" sideOffset={8}>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              className="group flex items-center justify-between rounded-md border border-cede-border-container bg-cede-bg-component/60 px-3 py-2 text-left transition-all duration-200 hover:border-cede-primary/40 hover:bg-cede-primary/10 hover:shadow-[inset_0_0_0_1px_rgba(94,166,255,0.25)]"
              onClick={() => {
                setIsDropdownOpen(false);
                setIsOpen(true);
                trackEvent(METRICS_EVENTS.HEADER_REWARDS_MENU_ITEM_CLICKED, {
                  item: "campaign_rewards",
                  totalClaimable: totalClaimable.toNumber(),
                  source: "header",
                });
              }}
            >
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-cede-primary/15 origin-center group-hover:animate-[iconShake_0.5s_ease-in-out]">
                  <Gift className="h-4 w-4 text-cede-primary transition-transform duration-200 group-hover:scale-105" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-cede-text-primary">Campaign Rewards</p>
                </div>
              </div>
              <span className="flex items-center gap-2 text-sm font-semibold text-cede-text-primary">
                <span className="flex items-center gap-1">${rewardsAmountText}</span>
                <ChevronRight className="h-4 w-4 text-cede-text-secondary transition-transform duration-200 group-hover:translate-x-0.5" />
              </span>
            </button>
            <button
              type="button"
              className="group flex items-center justify-between rounded-md border border-cede-border-container bg-cede-bg-component/60 px-3 py-2 text-left transition-all duration-200 hover:border-cede-success/40 hover:bg-cede-success/10 hover:shadow-[inset_0_0_0_1px_rgba(80,200,120,0.25)]"
              onClick={() => {
                setIsDropdownOpen(false);
                openCashback();
                trackEvent(METRICS_EVENTS.HEADER_REWARDS_MENU_ITEM_CLICKED, {
                  item: "cashback",
                  totalClaimable: totalClaimable.toNumber(),
                  source: "header",
                });
              }}
            >
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-cede-success/15 origin-center group-hover:animate-[iconShake_0.5s_ease-in-out]">
                  <BadgePercent className="h-4 w-4 text-cede-success transition-transform duration-200 group-hover:scale-105" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-cede-text-primary">Cashback</p>
                </div>
              </div>
              <span className="flex items-center gap-2 text-sm font-semibold text-cede-text-primary">
                <span className="flex items-center gap-1">${cashbackAmountText}</span>
                <ChevronRight className="h-4 w-4 text-cede-text-secondary transition-transform duration-200 group-hover:translate-x-0.5" />
              </span>
            </button>
          </div>
        </PopoverContent>
      </Popover>

      <RewardsModalContent
        open={isOpen}
        onClose={() => setIsOpen(false)}
        claimableSoonRewardDetails={populatedClaimableSoonRewardsDetails}
        rewardDetails={populatedRewardsDetails}
        vaultReward={formatBalance(totalRewardAmountFiat, { roundForFiat: true })}
        totalRewardFiat={campaignRewardsClaimable}
        rewardChainIds={rewardChainIds}
        address={address || ""}
        onClaim={() => claimReward(undefined)}
        isLoading={isLoading}
        isTMA={isTMA}
        isClaimCooldownActive={isClaimCooldownActive}
        hasClaimableItems={(claimablesData?.length ?? 0) > 0}
        hasMerklClaimable={merklClaimable.gt(0)}
        batchProgress={batchProgress}
        totalBatches={totalBatches}
        cashbackSummary={{
          claimableAmount: cashbackClaimable.toNumber(),
          currency: cashbackStatus?.claimable?.currency ?? null,
          onOpenCashback: openCashback,
        }}
      />
    </>
  );
};
